import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { site } from "@/data/store";
import { formatPrice, getProduct, getVariant } from "@/lib/store-helpers";
import { ProductGallery } from "@/components/ProductGallery";
import { VariantDetails, SingleVariant } from "@/components/VariantDetails";
import { BuyButton } from "@/components/BuyButton";
import { Testimonials } from "@/components/Testimonials";

const PlannerNotFound = () => (
  <main data-testid="product-not-found" className="mx-auto flex min-h-[60vh] max-w-[1440px] flex-col items-start justify-center px-5 py-24 sm:px-8 lg:px-12">
    <p className="text-[11px] uppercase tracking-[0.24em] text-black/50">Not found</p>
    <h1 className="mt-4 font-serif text-5xl font-light sm:text-6xl">Planner not found.</h1>
    <p className="mt-6 max-w-md text-black/60">The planner you're looking for doesn't exist or may have moved.</p>
    <Link to="/#shop" data-testid="back-to-shop-link" className="mt-10 inline-flex h-12 items-center border border-black px-8 text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 hover:bg-black hover:text-white">
      Back to shop
    </Link>
  </main>
);

const InfoList = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <h2 className="text-[11px] font-medium uppercase tracking-[0.24em] text-black/50">{title}</h2>
    <ul className="mt-4 space-y-2">
      {items.map((i) => (
        <li key={i} className="flex gap-3 text-sm text-black/75"><span className="mt-2 h-px w-3 shrink-0 bg-black" />{i}</li>
      ))}
    </ul>
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const product = getProduct(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = product ? `${product.name} — ${site.brandName}` : `Planner not found — ${site.brandName}`;
  }, [product]);

  if (!product) return <PlannerNotFound />;

  const variant = getVariant(product, params.get("variant"));
  const select = (id) => setParams({ variant: id }, { replace: true });

  return (
    <main data-testid={`product-detail-${product.slug}`} className="pb-28 lg:pb-0">
      <div className="mx-auto max-w-[1440px] px-5 pt-8 sm:px-8 lg:px-12">
        <Link to="/#shop" data-testid="back-link" className="nav-link inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.16em]">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> All planners
        </Link>
      </div>
      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-10 sm:px-8 lg:grid-cols-12 lg:gap-20 lg:px-12 lg:py-16">
        <div className="fade-up lg:col-span-7">
          <ProductGallery product={product} />
        </div>
        <div className="fade-up flex flex-col gap-10 lg:col-span-5" style={{ animationDelay: "120ms" }}>
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-black/50">{product.tagline}</p>
            <h1 data-testid="product-name" className="mt-4 font-serif text-5xl font-light leading-none sm:text-6xl">{product.name}</h1>
            <p data-testid="product-description" className="mt-6 text-base leading-relaxed text-black/65">{product.description}</p>
          </div>
          {product.variants.length > 1 ? (
            <VariantDetails product={product} selectedId={variant.id} onSelect={select} />
          ) : (
            <SingleVariant variant={variant} />
          )}
          <div className="hidden items-center justify-between gap-6 lg:flex">
            <p data-testid={`price-${product.slug}`} className="font-serif text-5xl leading-none">{formatPrice(variant.price)}</p>
            <BuyButton variant={variant} testId={`buy-button-${product.slug}`} className="min-w-[220px]" />
          </div>
          <div className="grid gap-10 border-t border-black/15 pt-10 sm:grid-cols-2">
            <InfoList title="What's inside" items={product.highlights} testId="product-highlights" />
            <InfoList title="Formats" items={product.formats} testId="product-formats" />
          </div>
        </div>
      </div>
      <Testimonials productSlug={product.slug} title={`What customers say about ${product.name}`} />
      <div data-testid="mobile-buy-bar" className="fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-[#F7F5F1]/95 backdrop-blur-md lg:hidden">
        <div className="flex items-center justify-between gap-4 px-5 py-3 sm:px-8">
          <div className="min-w-0">
            <p className="truncate text-[11px] uppercase tracking-[0.16em] text-black/55">{variant.name}</p>
            <p data-testid={`mobile-price-${product.slug}`} className="font-serif text-3xl leading-none">{formatPrice(variant.price)}</p>
          </div>
          <BuyButton variant={variant} testId={`mobile-buy-button-${product.slug}`} className="shrink-0" />
        </div>
      </div>
    </main>
  );
}
